import { MonthEndStatusPolicy } from "constants/monthEnd/MonthEndStatusPolicy";
import { MonthEndClosureConstant } from "presentation/constant/MonthEndClosure/MonthEndClosureConstant";
import { useMonthEndClosureVM } from "presentation/hook/MonthEndClosure/useMonthEndClosureVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { MonthEndClosureModel } from "presentation/model/MonthEndClosure/MonthEndClosureModel";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import DateTimePickerComponent from "presentation/view/components/DateTimePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NumberInputComponent from "presentation/view/components/NumberInputComponent";
import { memo, useCallback, useMemo } from "react";
import { FieldType, HPHCheckbox, IconButton, IFieldValue } from "veronica-ui-component/dist/component/core";
import { SidebarActionCross, Sidebarheader, SidebarTitle, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const MonthEndClosureEditPanel = ({ monthEndClosureState }: { monthEndClosureState: MonthEndClosureModel }) => {
    const MONTH_END_CLOSURE_CONSTANT = MonthEndClosureConstant.Table;
    const messageBarVM = useMessageBarVM();
    const monthEndClosureVM = useMonthEndClosureVM();
    const { currentEditRow } = monthEndClosureState;


    const handleSave = useCallback(async () => {
        if (!currentEditRow.fromDate) {
            messageBarVM.showError("From Date is mandatory.");
            return;
        }
        if (!currentEditRow.toDate) {
            messageBarVM.showError("To Date is mandatory.");
            return;
        }
        monthEndClosureVM.onShowLoading();
        monthEndClosureVM.onSave(currentEditRow).then((data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Save Successfully.");
                monthEndClosureVM.initMonthEndClosureList().then((data) => {
                    monthEndClosureVM.onHideLoading();
                }).catch(error => {
                    monthEndClosureVM.onHideLoading();
                });
            } else {
                messageBarVM.showError(data);
                monthEndClosureVM.onHideLoading();
            }
        })
    }, [currentEditRow, messageBarVM, monthEndClosureVM]);

    const handleReset = useCallback(() => {
        monthEndClosureVM.onResetClick();
    }, [monthEndClosureVM]);

    const handClose = useCallback(() => {
        monthEndClosureVM.onCloseClick();
    }, [monthEndClosureVM]);

    const isFieldDisable = useCallback(() => {
        if ((currentEditRow.status === MonthEndStatusPolicy.STATUS_ERROR
            || currentEditRow.status === MonthEndStatusPolicy.STATUS_WIP
            || currentEditRow.status === MonthEndStatusPolicy.STATUS_NEW)
        ) return false;
        return true;
    }, [currentEditRow.status]);

    const onNumberFieldChange = useCallback((e: any, fieldName: string, fieldValue: any) => {
        monthEndClosureVM.onHeaderFieldChange(fieldName, fieldValue);
    }, [monthEndClosureVM]);

    const memoYear = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.year?.toString() ?? ""}
                fieldValue={currentEditRow?.year}
                fieldLabel={MONTH_END_CLOSURE_CONSTANT.YEAR}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'year'}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    monthEndClosureVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.YEAR, currentEditRow?.year, monthEndClosureVM])

    const memoMonth = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.month?.toString() ?? ""}
                fieldValue={currentEditRow?.month}
                fieldLabel={MONTH_END_CLOSURE_CONSTANT.MONTH}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'month'}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    monthEndClosureVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.MONTH, currentEditRow?.month, monthEndClosureVM])

    const memoFromDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={isFieldDisable()}
                optional={false}
                label={MONTH_END_CLOSURE_CONSTANT.FROM_DATE}
                width="300px"
                date={currentEditRow?.fromDate}
                fieldName="fromDate"
                errorMessage={""}
                onDateChange={monthEndClosureVM.onHeaderFieldChange} />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.FROM_DATE, currentEditRow?.fromDate, isFieldDisable, monthEndClosureVM.onHeaderFieldChange])

    const memoToDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={isFieldDisable()}
                optional={false}
                label={MONTH_END_CLOSURE_CONSTANT.TO_DATE}
                width="300px"
                date={currentEditRow?.toDate}
                fieldName="toDate"
                errorMessage={""}
                onDateChange={monthEndClosureVM.onHeaderFieldChange} />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.TO_DATE, currentEditRow?.toDate, isFieldDisable, monthEndClosureVM.onHeaderFieldChange])


    const memoStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentEditRow?.status}
                fieldValue={currentEditRow?.status}
                fieldLabel={MONTH_END_CLOSURE_CONSTANT.STATUS}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'status'}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    monthEndClosureVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.STATUS, currentEditRow?.status, monthEndClosureVM])

    const memoTurnover = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={MONTH_END_CLOSURE_CONSTANT.TURNOVER}
                maxLength={60}
                fieldName={"turnover"}
                value={currentEditRow.turnover ?? ''}
                errorMessage={''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.TURNOVER, currentEditRow.turnover, onNumberFieldChange])

    const memoEstimation = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={MONTH_END_CLOSURE_CONSTANT.ESTIMATION}
                maxLength={60}
                fieldName={"estimation"}
                value={currentEditRow.estimation ?? ''}
                errorMessage={''}
                disabled={true}
                decimalPlaces={2}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.ESTIMATION, currentEditRow.estimation, onNumberFieldChange])


    const memoUpdatedDateTime = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={true}
                optional={true}
                label={MONTH_END_CLOSURE_CONSTANT.UPDATE_DATETIME}
                width="300px"
                date={currentEditRow?.updatedDateTime}
                fieldName="updatedDateTime"
                errorMessage={""}
                onDateChange={monthEndClosureVM.onHeaderFieldChange} />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.UPDATE_DATETIME, currentEditRow?.updatedDateTime, monthEndClosureVM.onHeaderFieldChange])

    const memoActiveInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "24px" }}>
            <NbisCommonField
                fieldKey={"activeInd"}
                requiredFieldList={[]}
                fieldLabel={MONTH_END_CLOSURE_CONSTANT.ACTIVE_IND}
                fieldType={FieldType.TEXT}
                isReadOnly={true}
                isShowOptional={false}
                onFieldChange={() => { }} />
            <HPHCheckbox
                label={""}
                checked={currentEditRow?.activeInd === 'Y'}
                disabled={isFieldDisable()}
                onChange={(e) => monthEndClosureVM.onCheckboxChange(e.checked, "activeInd")}
            />
        </div>
        , [MONTH_END_CLOSURE_CONSTANT.ACTIVE_IND, currentEditRow?.activeInd, isFieldDisable, monthEndClosureVM])

    return <>
        <div className='side-form-content-left-wrapper'>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>
                        <Sidebarheader style={{ width: '100%' }}>
                            <SidebarTitle>
                                {!isFieldDisable() ? "Update Month End" : "Month End Info"}
                            </SidebarTitle>
                            <SidebarActionCross>
                                <>
                                    {!isFieldDisable() && <IconButton fileName="Icon-reset" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Reset'} onClick={() => handleReset()} />}
                                    {!isFieldDisable() && <IconButton fileName="Icon-tick" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Save'} onClick={() => handleSave()} />}
                                    <IconButton fileName="Icon-cross" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement='bottom' toolTipText={'Close'} onClick={() => handClose()} />
                                </>
                            </SidebarActionCross>

                        </Sidebarheader>

                    </StyledSidebar>
                </div>
            </div>
            <div className={'add-edit-form'} style={{ height: '78vh', maxHeight: '78vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoYear}
                            {memoMonth}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoFromDate}
                            {memoToDate}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoStatus}
                            {memoUpdatedDateTime}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoTurnover}
                            {memoEstimation}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoActiveInd}
                        </CriteriaItemContainer>


                    </div>
                </div>
            </div>

        </div>
    </>
}

export default memo(MonthEndClosureEditPanel);
